import { useCallback, useEffect, useMemo, useState } from 'react';

interface UseTableNavigationReturn {
  zoomLevel: number;
  currentAction: unknown;
  isUndoDisabled: boolean;
  isRedoDisabled: boolean;
  onRedo: () => void;
  onUndo: () => void;
  addAction: (item: unknown) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  clearHistory: () => void;
}

export const useTableNavigation = (): UseTableNavigationReturn => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [actionHistory, setActionHistory] = useState<unknown[]>([]);
  const [zoomLevel, setZoomLevel] = useState<number>(1);

  const zoomIn = (): void => setZoomLevel(prev => Math.min(prev + 0.1, 2));
  const zoomOut = (): void => setZoomLevel(prev => Math.max(prev - 0.1, 0.5));

  const currentAction = useMemo(() => {
    return actionHistory[activeStep];
  }, [actionHistory, activeStep]);

  const isUndoDisabled = activeStep <= 0;
  const isRedoDisabled =
    activeStep === (actionHistory.length === 0 ? actionHistory.length : actionHistory.length - 1);

  function onRedo(): void {
    if (isRedoDisabled) return;

    setActiveStep(prev => prev + 1);
  }

  function onUndo(): void {
    if (isUndoDisabled) return;

    setActiveStep(prev => prev - 1);
  }

  const updateStep = useCallback(() => {
    setActiveStep(actionHistory.length - 1);
  }, [actionHistory, activeStep]);

  const clearHistory = (): void => {
    setActionHistory([]);
    setActiveStep(0);
  };

  useEffect(() => {
    updateStep();
  }, [actionHistory]);

  const addAction = useCallback(
    (item: unknown) => {
      setActionHistory(prev => {
        if (activeStep !== actionHistory.length - 1) return [...prev?.slice(0, activeStep), item];

        return [...prev, item];
      });
    },
    [actionHistory, activeStep]
  );

  useEffect(() => {
    return () => {
      setActiveStep(0);
      setActionHistory([]);
    };
  }, []);

  return {
    zoomLevel,
    currentAction,
    isUndoDisabled,
    isRedoDisabled,
    onRedo,
    onUndo,
    addAction,
    zoomIn,
    zoomOut,
    clearHistory
  };
};
